import React, { useState } from 'react';
import {
  ListItem,
  ListItemText,
  Typography,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Tooltip,
  Avatar,
  Dialog,
  DialogContent,
  useMediaQuery,
} from '@mui/material';
import { Delete, Edit, MoreVert, Close, ArrowBack, ArrowForward } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { useDeleteFeedbackMutation } from '../feedbackApiSlice';
import UserAvatar from '../../../common/UserAvatar';
import ChatBubble from './ChatBubble';
import LinkPreview from './LinkPreview';
import { formatTime } from '../../../utils/dateUtils';
import { useSelector } from 'react-redux';
import { selectCurrentUserEmpCode } from '../../Auth/authSlice';
import AttachAvatar from '../../../common/AttachAvatar';
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../../context/Ability';

const FeedbackItem = ({ feedback, user, setEditingFeedback }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const userEmpCode = useSelector(selectCurrentUserEmpCode);
  const [deleteFeedback] = useDeleteFeedbackMutation();
  const ability = useAbility(AbilityContext);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAttachmentIndex, setSelectedAttachmentIndex] = useState(null);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEditFeedback = () => {
    setEditingFeedback({
      feedbackId: feedback._id,
      feedbackText: feedback.feedbackText,
      priority: feedback.priority,
      category: feedback.category,
      attachments: feedback.attachments,
    });
    handleClose();
  };

  const handleDeleteFeedback = async () => {
    await toast.promise(deleteFeedback(feedback._id), {
      loading: 'Deleting feedback...',
      success: 'Feedback deleted successfully!',
      error: (err) => err.data?.message || 'Error deleting feedback. Please try again.',
    });
    handleClose();
  };

  const handleAttachmentClick = (index) => {
    setSelectedAttachmentIndex(index);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedAttachmentIndex(null);
  };
  const handlePreviousAttachment = () => {
    setSelectedAttachmentIndex(
      (prevIndex) => (prevIndex - 1 + feedback.attachments.length) % feedback.attachments.length
    );
  };

  const handleNextAttachment = () => {
    setSelectedAttachmentIndex((prevIndex) => (prevIndex + 1) % feedback.attachments.length);
  };

  return (
    <>
      <ListItem alignItems='flex-start' sx={{ flexDirection: 'row' }}>
        <Box display={'flex'} flexDirection={'row'}>
          <UserAvatar
            showStatus={false}
            empCode={feedback?.userEmpCode}
            src={feedback.authorUser?.photo}
            sx={{ height: '30px', width: '30px', mr: 1, marginTop: '5px', ml: '-10px' }}
          />
          <ListItemText
            primary={
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-end',
                }}
              >
                <Typography variant='caption' color='#555' sx={{ fontWeight: 'bold', fontSize: '12px' }}>
                  <>
                    {feedback.authorUser?.username}, {formatTime(feedback?.datePosted)}{' '}
                  </>
                </Typography>
                {(feedback.userEmpCode === userEmpCode || ability.can('read', 'Admin')) && (
                  <IconButton
                    aria-label='more'
                    aria-controls='short-menu'
                    aria-haspopup='true'
                    onClick={handleClick}
                    size='small'
                    sx={{ mt: '-3px' }}
                  >
                    <MoreVert sx={{ fontSize: '14px' }} />
                  </IconButton>
                )}
              </Box>
            }
            secondary={
              <>
                {feedback.attachments && feedback.attachments.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      mb: 1,
                      justifyContent: 'flex-start',
                      alignItems: 'flex-end',
                    }}
                  >
                    {feedback.attachments.map((attachment, index) => (
                      <Box key={index} onClick={() => handleAttachmentClick(index)}>
                        <Tooltip title={attachment.split('/').pop()} arrow>
                          <AttachAvatar
                            src={attachment}
                            controls={false}
                            autoPlay={false}
                            muted={true}
                            variant='rounded'
                            className='attachment-avatar'
                            sx={{
                              width: 100,
                              height: 100,
                              margin: 0.5,
                              objectFit: 'cover',
                              cursor: 'default',
                              aspectRatio: '1 / 1',
                            }}
                          />
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                )}
                <LinkPreview feedback={feedback} />
                <ChatBubble isOwnMessage={feedback.userEmpCode === userEmpCode} feedback={feedback} />
              </>
            }
          />
        </Box>
        <Menu id='short-menu' anchorEl={anchorEl} keepMounted open={open} onClose={handleClose}>
          <MenuItem onClick={handleEditFeedback}>
            <Edit sx={{ marginRight: 1, fontSize: '14px' }} /> Edit
          </MenuItem>
          <MenuItem onClick={handleDeleteFeedback}>
            <Delete sx={{ marginRight: 1, fontSize: '14px' }} /> Delete
          </MenuItem>
        </Menu>
      </ListItem>
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth='lg'
        fullWidth
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '12px',
          },
        }}
      >
        <DialogContent
          sx={{
            padding: 0,
            display: 'flex',
            justifyContent: 'start',
            alignItems: 'center',
            height: isMobile ? '50vh' : '75vh',
            width: '100%',
            overflowX: isMobile ? 'auto' : 'hidden',
          }}
        >
          <IconButton
            onClick={handleDialogClose}
            sx={{
              position: 'absolute',
              top: isMobile ? 8 : 16,
              right: isMobile ? 8 : 16,
              background: 'lightgrey',
              zIndex: 2,
            }}
          >
            <Close fontSize={isMobile ? 'small' : 'medium'} sx={{ color: 'black', m: '-5px' }} />
          </IconButton>

          {selectedAttachmentIndex !== null && (
            <>
              <AttachAvatar
                src={feedback.attachments[selectedAttachmentIndex]}
                controls
                autoPlay
                variant='rounded'
                sx={{
                  width: 'auto',
                  height: '100%',
                  objectFit: 'contain',
                  borderRadius: '12px',
                }}
              />

              {feedback.attachments.length > 1 && (
                <>
                  <IconButton
                    onClick={handlePreviousAttachment}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: isMobile ? 8 : 16,
                      color: 'white',
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      transform: 'translateY(-50%)',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      },
                      zIndex: 2,
                    }}
                  >
                    <ArrowBack fontSize={isMobile ? 'small' : 'medium'} />
                  </IconButton>

                  <IconButton
                    onClick={handleNextAttachment}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      right: isMobile ? 8 : 16,
                      color: 'white',
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      transform: 'translateY(-50%)',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      },
                      zIndex: 2,
                    }}
                  >
                    <ArrowForward fontSize={isMobile ? 'small' : 'medium'} />
                  </IconButton>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeedbackItem;
